import type { IntlShape } from 'react-intl';

import type { MinMax } from '../../../../core/types/min-max.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { FormatCategory } from '../../../hooks/useFormatCategory.js';
import { isCategorical } from '../../../types/guards.js';
import type {
  AxisIdToFormatter,
  AxisIdToScaleDomain,
  MapDatapoints,
  XYChartAxisInternal,
} from '../../../types/xy-chart-internals.js';
import type {
  HoveredDatapoint,
  HoveredSeries,
  HoveredSeriesToMetadata,
} from '../../../utils/type-guards.js';
import { getRelativeAccessor } from '../../../utils/value-representation.js';
import { getTooltipFormatter } from '../utils/get-tooltip-formatter.js';
import type { XYChartTooltipBodyProps } from '../XYChartTooltipBody/XYChartTooltipBody.js';
import { getHoveredPointTooltipBodyContent } from './hooks/getHoveredPointTooltipBodyContent.js';

export interface BuildHoveredPointTooltipBodyPropsArgs<
  T extends HoveredSeries,
  K extends HoveredDatapoint<T>,
> {
  datapoint: K;
  series: T;
  seriesToMetadata: HoveredSeriesToMetadata<T>;
  axisIdToFormatter: AxisIdToFormatter;
  axisIdToScaleDomain: AxisIdToScaleDomain;
  datapointMetadataMap: MapDatapoints;
  formatCategory: FormatCategory;
  intl: IntlShape;
}

export const buildHoveredPointTooltipBodyProps = <
  T extends HoveredSeries,
  K extends HoveredDatapoint<T>,
>({
  datapoint,
  series,
  seriesToMetadata,
  axisIdToFormatter,
  axisIdToScaleDomain,
  datapointMetadataMap,
  formatCategory,
  intl,
}: BuildHoveredPointTooltipBodyPropsArgs<T, K>): XYChartTooltipBodyProps => {
  const { xAxis, yAxis } = safeGet(seriesToMetadata, series);

  const xFormatter = getTooltipFormatter(xAxis, axisIdToFormatter, intl);
  const yFormatter = getTooltipFormatter(yAxis, axisIdToFormatter, intl);
  const datapointMetadata = datapointMetadataMap.get(datapoint);
  const binDomain = datapointMetadata?.binDomain;

  const isRelative = typeof binDomain === 'number' && binDomain;

  const yDomain =
    isRelative && datapointMetadata?.baseline === 'y'
      ? {
          min: binDomain * -1,
          max: binDomain,
        }
      : (safeGet(axisIdToScaleDomain, xAxis.id) as MinMax);

  const xDomain =
    isRelative && datapointMetadata?.baseline === 'x'
      ? {
          min: binDomain * -1,
          max: binDomain,
        }
      : (safeGet(axisIdToScaleDomain, yAxis.id) as MinMax);

  const { x0, x1, y0, y1 } = datapoint;

  const buildCategoricalStringFormatter = (axis: XYChartAxisInternal) =>
    isCategorical(axis.type)
      ? (value: string) => formatCategory(axis, value) ?? value
      : undefined;

  const xResolvedFormatter =
    buildCategoricalStringFormatter(xAxis) ?? xFormatter;
  const yResolvedFormatter =
    buildCategoricalStringFormatter(yAxis) ?? yFormatter;

  const { text: textX, duration: durationX } =
    getHoveredPointTooltipBodyContent(
      intl,
      x0,
      x1,
      xResolvedFormatter,
      getRelativeAccessor(xAxis, yDomain),
    );

  const { text: textY, duration: durationY } =
    getHoveredPointTooltipBodyContent(
      intl,
      y0,
      y1,
      yResolvedFormatter,
      getRelativeAccessor(yAxis, xDomain),
    );

  return {
    xAxisLabel: xAxis.label,
    yAxisLabel: yAxis.label,
    xAxisValue: textX,
    yAxisValue: textY,
    xAxisDuration: durationX,
    yAxisDuration: durationY,
  };
};
